import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { ShieldCheck, Clock, BadgeCheck, CreditCard, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { formatEUR } from "../lib/contact";
import { toast } from "sonner";

const STEPS = { FORM: "form", PAYMENT: "payment", SUCCESS: "success" };
const RESERVATION_FEE = 200;

const ReservationDialog = ({ open, onOpenChange, vehicle }) => {
  const [step, setStep] = React.useState(STEPS.FORM);
  const [form, setForm] = React.useState({
    name: "",
    phone: "",
    email: "",
    preferred_date: "",
    message: "",
  });
  const [processing, setProcessing] = React.useState(false);
  const [createdId, setCreatedId] = React.useState(null);

  // Reset state when dialog re-opens
  React.useEffect(() => {
    if (open) {
      setStep(STEPS.FORM);
      setForm({ name: "", phone: "", email: "", preferred_date: "", message: "" });
      setProcessing(false);
      setCreatedId(null);
    }
  }, [open]);

  if (!vehicle) return null;
  const vehicleName = `${vehicle.make} ${vehicle.model}`;

  const goToPayment = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Bitte Name und Telefonnummer ausfüllen.");
      return;
    }
    setStep(STEPS.PAYMENT);
  };

  const confirmPayment = async () => {
    setProcessing(true);
    // Simulate payment processing 1.5s — clearly a prototype demo step
    await new Promise((r) => setTimeout(r, 1500));
    try {
      const { data } = await api.post("/reservations", {
        vehicle_id: vehicle.id,
        vehicle_name: vehicleName,
        vehicle_price: vehicle.price,
        ...form,
      });
      setCreatedId(data.id);
      setStep(STEPS.SUCCESS);
      toast.success("Reservierung bestätigt");
    } catch (err) {
      toast.error("Reservierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="reservation-dialog"
        className="bg-[var(--na-bg-2)] border-white/10 text-white max-w-lg p-0 overflow-hidden rounded-3xl"
      >
        {/* Header banner */}
        <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-[var(--na-red)]/15 via-transparent to-transparent border-b border-white/5">
          <DialogHeader>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--na-red)] font-semibold">Reservierung</p>
            <DialogTitle className="font-display text-2xl mt-1 tracking-tight">
              {step === STEPS.SUCCESS ? "Reservierung bestätigt" : "Fahrzeug reservieren"}
            </DialogTitle>
            <p className="text-sm text-zinc-400 mt-1">{vehicleName}</p>
          </DialogHeader>
        </div>

        <div className="px-6 py-6">
          {step === STEPS.FORM && (
            <form onSubmit={goToPayment} className="space-y-5">
              <div className="rounded-2xl bg-[var(--na-bg-3)] border border-white/10 p-4 space-y-3">
                <p className="text-sm text-zinc-200 leading-relaxed">
                  Reservieren Sie dieses Fahrzeug für <span className="text-white font-semibold">7 Tage</span> mit einer
                  Anzahlung von <span className="text-[var(--na-red)] font-bold">€200</span>.
                </p>
                <div className="text-xs text-zinc-400 leading-relaxed flex items-start gap-2 pt-2 border-t border-white/5">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[var(--na-red)]" />
                  Die Reservierungsgebühr wird beim Kaufpreis angerechnet. Wenn der Kunde das Fahrzeug nach Ablauf
                  der Reservierung nicht kauft oder den Termin nicht wahrnimmt, kann die Reservierung verfallen.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Name *" testId="reservation-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <F label="Telefon *" testId="reservation-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <F label="E-Mail" testId="reservation-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <F label="Wunschdatum" testId="reservation-date" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} />
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">
                  Optionale Nachricht
                </Label>
                <textarea
                  data-testid="reservation-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  placeholder="Wann möchten Sie das Fahrzeug besichtigen oder Probe fahren?"
                  className="w-full rounded-xl bg-[var(--na-bg)] border border-white/10 text-white px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[var(--na-red)]"
                />
              </div>

              <Button
                type="submit"
                data-testid="reservation-continue"
                className="w-full bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white font-medium rounded-2xl py-3.5 tracking-wide red-glow"
              >
                Jetzt für €200 reservieren <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}

          {step === STEPS.PAYMENT && (
            <div className="space-y-5">
              <div className="rounded-2xl bg-[var(--na-bg-3)] border border-white/10 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Reservierungsgebühr</span>
                  <span className="font-display text-3xl font-bold text-white tracking-tight">{formatEUR(RESERVATION_FEE)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-zinc-400 pt-3 border-t border-white/5">
                  <span>Fahrzeug</span>
                  <span className="text-white">{vehicleName}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-zinc-400">
                  <span>Reservierungsdauer</span>
                  <span className="text-white inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 7 Tage</span>
                </div>
              </div>

              <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/5 p-4 text-xs text-yellow-200/90 leading-relaxed">
                <strong className="text-yellow-300">Prototype-Hinweis:</strong> Dies ist eine Demo-Zahlung. Es wird keine
                echte Belastung durchgeführt. In der finalen Version wird Stripe Checkout für die sichere Abwicklung integriert.
              </div>

              <Button
                onClick={confirmPayment}
                disabled={processing}
                data-testid="reservation-pay"
                className="w-full bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white font-medium rounded-2xl py-3.5 tracking-wide red-glow"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Zahlung wird verarbeitet…</>
                ) : (
                  <><CreditCard className="w-4 h-4 mr-2" /> Demo-Zahlung €200 bestätigen</>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep(STEPS.FORM)}
                disabled={processing}
                className="w-full text-xs text-zinc-400 hover:text-white transition-colors py-2"
              >
                ← Zurück
              </button>
            </div>
          )}

          {step === STEPS.SUCCESS && (
            <div className="space-y-5 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--na-red)]/15 text-[var(--na-red)] flex items-center justify-center">
                <BadgeCheck className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold text-white">Vielen Dank!</h3>
                <p className="text-sm text-zinc-400 mt-2 max-w-sm mx-auto leading-relaxed">
                  Ihre Reservierung für {vehicleName} ist eingegangen. Wir melden uns innerhalb eines Werktags zur
                  Terminvereinbarung. Reservierungs-Nr: <span className="text-white font-mono">{createdId?.slice(0, 8)}</span>
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[var(--na-bg-3)] p-4 text-left space-y-2 text-xs">
                <Row k="Fahrzeug" v={vehicleName} />
                <Row k="Anzahlung" v={formatEUR(RESERVATION_FEE)} />
                <Row k="Reserviert bis" v={new Date(Date.now() + 7 * 24 * 3600 * 1000).toLocaleDateString("de-AT")} />
                <Row k="Status" v="Neu (in Bearbeitung)" />
              </div>
              <Button
                onClick={() => onOpenChange(false)}
                data-testid="reservation-close"
                className="w-full bg-white text-[var(--na-bg)] hover:bg-zinc-200 rounded-2xl py-3 font-medium"
              >
                Schließen
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const F = ({ label, value, onChange, testId, type = "text" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    <Input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[var(--na-bg)] border-white/10 text-white rounded-xl focus-visible:ring-[var(--na-red)] focus-visible:border-[var(--na-red)]"
    />
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-zinc-500 uppercase tracking-widest text-[10px]">{k}</span>
    <span className="text-white text-right">{v}</span>
  </div>
);

export default ReservationDialog;
